/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button, Stack, useMantineTheme, type ButtonProps } from '@mantine/core';
import { type ContextModalProps } from '@mantine/modals';
import type { OpenContextModal } from '@mantine/modals/lib/context';
import { useTranslation } from 'react-i18next';

import { FooterlessModalFooter } from '@fai/ui/mantine-modal/helpers';

export type ConfirmModalType = OpenContextModal<Partial<ConfirmModalProps>>;

interface ConfirmModalProps {
  modalBody?: JSX.Element;
  cancelProps?: ButtonProps & React.ComponentPropsWithoutRef<'button'>;
  confirmProps?: ButtonProps & React.ComponentPropsWithoutRef<'button'>;
  onConfirm?: VoidFunction;
  labels?: {
    cancel?: JSX.Element | string | number;
    confirm?: JSX.Element | string | number;
  };
}

export default function ConfirmModal({
  context,
  id,
  innerProps,
}: ContextModalProps<ConfirmModalProps>) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const closeModal = () => context.closeModal(id);

  return (
    <Stack gap={theme.pf.layout.xs}>
      {innerProps.modalBody}

      <FooterlessModalFooter>
        <Button variant="transparent" onClick={closeModal} {...innerProps.cancelProps}>
          {innerProps.labels?.cancel ?? t('component.cancel')}
        </Button>

        <Button
          theme="cloud"
          onClick={() => {
            if (innerProps.onConfirm) innerProps.onConfirm();

            closeModal();
          }}
          {...innerProps.confirmProps}
        >
          {innerProps.labels?.confirm ?? t('component.confirm')}
        </Button>
      </FooterlessModalFooter>
    </Stack>
  );
}
